import { CopyButton } from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";

/** A labelled extra line under the main result: another spelling, a regional form. */
export interface ResultLine {
  label: string;
  text: string;
  /** Language tag for the text, when it is in the target language. */
  lang?: string;
}

export interface NumberResultStrings {
  heading: string;
  copy: string;
  copied: string;
  rulesHeading: string;
}

interface NumberResultProps {
  strings: NumberResultStrings;
  /** BCP 47 tag of the spelled-out words. */
  lang: string;
  /** Text direction of the words; set "rtl" for Hebrew and Arabic. */
  dir?: "ltr" | "rtl";
  words: string;
  /** Shown under the words (an abbreviation, a digit form). */
  detail?: ResultLine;
  lines: ResultLine[];
  notes: string[];
}

/** The result card shared by the spelled-out-number converters. */
export function NumberResult({
  strings,
  lang,
  dir,
  words,
  detail,
  lines,
  notes,
}: NumberResultProps) {
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.heading}</h2>
      <p
        className="break-words font-semibold text-2xl text-foreground"
        dir={dir}
        lang={lang}
      >
        {words}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={words}
        />
        {detail ? (
          <span className="text-foreground text-sm">
            {detail.label}:{" "}
            <span className="font-mono" lang={detail.lang}>
              {detail.text}
            </span>
          </span>
        ) : null}
      </div>
      {lines.length > 0 ? (
        <ul className="space-y-1 text-foreground text-sm">
          {lines.map((line) => (
            <li key={`${line.label}-${line.text}`}>
              {line.label}: <span lang={line.lang ?? lang}>{line.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {notes.length > 0 ? (
        <div className="space-y-2">
          <h3 className="font-medium text-foreground text-sm">
            {strings.rulesHeading}
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm leading-relaxed">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
